// imports
import { useAtom } from 'jotai';
import { forwardRef, ForwardedRef, useState, useEffect } from 'react';

// lib files
import { StationData } from '@/lib/types/api/stations-data';
import { useHasMounted } from '@/lib/hooks';
import { useIdentity } from '@/lib/hooks/useIdentity';
import {  navigationAtom, NavigationStateEnum } from '@/lib/atoms/navigation';

// components
import SignInButton from '@/components/Navigation/SignInButton';
import SignInMenu from '@/components/Navigation/SignInMenu';
import UserImageInitial from '@/components/Navigation/UserImageInitial';
import UserMenu from '@/components/Navigation/UserMenu';

// svgs
import CloseIcon from '@/public/svg/close.svg';

// styles
import styles from '@/components/Navigation/SignInOrProfile.module.scss';

interface SignInOrProfileProps {
  isSVP: boolean;
  stationData?: StationData;
  ketchEnabled?: boolean;
}

const SignInOrProfile = forwardRef(function SignInOrProfile(props: SignInOrProfileProps, ref: ForwardedRef<HTMLDivElement>) {
  const { isSVP, stationData, ketchEnabled } = props;
  const hasMounted = useHasMounted();

  const { state, isSignedIn, isResolving } = useIdentity()
  const profile = state.profile
  const defaultClassName = isSVP ? styles.sign_in_or_profile_svp : styles.sign_in_or_profile;
  const [classNames, setClassNames] = useState(`${defaultClassName} ${styles.loading}`);

  const [navigation, setNavigation] = useAtom(navigationAtom);
  const userMenuIsOpen = navigation === NavigationStateEnum.UserMenuOpen;

  const handleClick = () => {
    setNavigation(userMenuIsOpen ? NavigationStateEnum.Default : NavigationStateEnum.UserMenuOpen);
  }

  const ariaLabel = userMenuIsOpen ? 'Close User Menu' : 'Open User Menu';

  useEffect(() => {
    const isReady = hasMounted && !isResolving
    const hasProfile = isSignedIn

    switch (true) {
      case isReady && hasProfile && userMenuIsOpen:
        setClassNames(`${defaultClassName} ${styles.profile} ${styles.user_menu_is_open}`);
        break;
      case isReady && hasProfile:
        setClassNames(`${defaultClassName} ${styles.profile}`);
        break;
      case isReady && !hasProfile:
        setClassNames(`${defaultClassName} ${styles.sign_in}`);
        break;
      default:
        setClassNames(`${defaultClassName} ${styles.loading}`);
        break;
    }
  }, [isSignedIn, isResolving, hasMounted, defaultClassName, userMenuIsOpen]);

  return (
    <div className={classNames} ref={ref}>
      {(!hasMounted || isResolving) ? null : (
      <>
        { profile ? (
        <>
          <button aria-label={ariaLabel} onClick={handleClick} className={styles.user_button}>
            <CloseIcon className={styles.close_icon} aria-hidden={true} />
            <UserImageInitial profile={profile} className={styles.user_image_inital} />
          </button>
          <UserMenu
            className={styles.user_menu}
            isLocalized={!!(stationData?.id)}
            isStationPassportEnabled={!!(stationData?.attributes.passport_enabled)}
            ketchEnabled={ketchEnabled}
          />
        </>
        ) : (
        <>
          <SignInButton size="min" />
          <SignInMenu stationData={stationData} className={styles.sign_in_menu}/>
        </>
        )
        }
      </>
      )}
    </div>
  );
});

export default SignInOrProfile;
